import { SupabaseClient, Session } from '@supabase/supabase-js';

export interface AuthLinkProcessResult {
  success: boolean;
  session: Session | null;
  error: Error | null;
}

/**
 * Process authentication links - handles both PKCE flow (code in query params)
 * and legacy hash-based token approach.
 */
export async function processAuthLink(
  supabase: SupabaseClient,
  location: { search: string; hash: string },
  expectedHashType?: string
): Promise<AuthLinkProcessResult> {
  const result: AuthLinkProcessResult = {
    success: false,
    session: null,
    error: null
  };

  try {
    // First, check for PKCE flow (code parameter in query string)
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');

    if (code) {
      console.log('[AuthLinkUtils] Detected code parameter, using PKCE flow. Code:', code);
      const { data, error } = await supabase.auth.exchangeCodeForSession(code);

      if (error) {
        console.error('[AuthLinkUtils] Error exchanging code for session:', error);
        throw error;
      }

      if (data?.session) {
        console.log('[AuthLinkUtils] Successfully exchanged code for session:', data.session);
        result.success = true;
        result.session = data.session;
        return result;
      } else {
        console.log('[AuthLinkUtils] No session returned after exchanging code, but no error.');
        // This case might indicate an issue or an already used code.
        // Let it fall through to hash processing or default to failure.
      }
    }

    // If no code or code exchange failed/didn't return a session, try hash-based approach as fallback
    const hash = location.hash;
    if (!hash) {
      console.log('[AuthLinkUtils] No PKCE code and no hash parameters found.');
      return result; // No code, no hash, nothing to process
    }

    console.log('[AuthLinkUtils] Processing hash parameters. Hash:', hash);

    // Handle potential double hash (e.g., #/auth/reset-password#access_token=...)
    // The first part of a double hash is usually the route, the second part contains params.
    const firstHashCharIndex = hash.indexOf('#');
    let paramsString = '';

    if (firstHashCharIndex !== -1) {
        const secondHashCharIndex = hash.indexOf('#', firstHashCharIndex + 1);
        if (secondHashCharIndex !== -1) {
            // Double hash: #route#params
            paramsString = hash.substring(secondHashCharIndex + 1);
            console.log('[AuthLinkUtils] Double hash detected. Params string:', paramsString);
        } else {
            // Single hash: #params or #route (if no params)
            // If it's just a route like #/auth/reset-password, it won't have access_token
            paramsString = hash.substring(firstHashCharIndex + 1);
            console.log('[AuthLinkUtils] Single hash detected. Params string:', paramsString);
        }
    }

    const hashParams = new URLSearchParams(paramsString);
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const type = hashParams.get('type');

    console.log('[AuthLinkUtils] Parsed from hash - accessToken:', accessToken, 'refreshToken:', refreshToken, 'type:', type);

    if (!accessToken) {
      console.log('[AuthLinkUtils] No access token found in hash after PKCE check.');
      // If PKCE was attempted and failed, and hash also fails, this is the final failure point.
      return result;
    }

    // If expectedHashType is provided, validate the type
    if (expectedHashType && type !== expectedHashType) {
      console.log(`[AuthLinkUtils] Hash type "${type}" doesn't match expected type "${expectedHashType}"`);
      return result; // Type mismatch
    }

    // Set the session in Supabase using the tokens from hash
    console.log('[AuthLinkUtils] Attempting to set session using tokens from hash.');
    const { data: { session }, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken || '' // Refresh token might be optional
    });

    if (error) {
      console.error('[AuthLinkUtils] Error setting session from hash tokens:', error);
      throw error;
    }

    if (session) {
      console.log('[AuthLinkUtils] Successfully set session from hash tokens:', session);
      result.success = true;
      result.session = session;
    } else {
      console.log('[AuthLinkUtils] No session returned after setting session from hash tokens, but no error.');
    }

    return result;
  } catch (error) {
    console.error('[AuthLinkUtils] Catch block: Error processing auth link:', error);
    result.error = error as Error;
    return result;
  }
}

/**
 * Clean up the URL after processing an auth link.
 * Removes code parameter and/or hash as needed.
 */
export function cleanupAuthLinkUrl(currentLocation: Location, preserveHashForRouting = false): void {
  const url = new URL(window.location.href); // Use window.location.href to get the full current URL

  // Remove 'code' query parameter if it exists
  if (url.searchParams.has('code')) {
    url.searchParams.delete('code');
    console.log('[AuthLinkUtils] Removed "code" from URL query params.');
  }

  // Handle the hash
  if (!preserveHashForRouting) {
    // If not preserving hash (e.g., for invite completion), clear it.
    url.hash = '';
    console.log('[AuthLinkUtils] Cleared URL hash.');
  } else {
    // If preserving hash (e.g., for password reset where hash is the route),
    // we need to be careful. If the original hash contained tokens (e.g. #access_token=...),
    // those should be removed. If it was a double hash (#route#tokens), we want to keep #route.
    // The `processAuthLink` function doesn't modify the hash itself, so `currentLocation.hash`
    // is the original hash. If it was a double hash, we only want the first part.
    const firstHashCharIndex = currentLocation.hash.indexOf('#');
    if (firstHashCharIndex !== -1) {
        const secondHashCharIndex = currentLocation.hash.indexOf('#', firstHashCharIndex + 1);
        if (secondHashCharIndex !== -1) {
            // It was a double hash, keep only the first part (the route)
            url.hash = currentLocation.hash.substring(0, secondHashCharIndex);
            console.log('[AuthLinkUtils] Preserved route part of double hash:', url.hash);
        } else {
            // It was a single hash. If it contained tokens, it should be cleared.
            // If it was just a route, it's fine.
            // For simplicity, if we are preserving hash, we assume it's for routing.
            // The `processAuthLink` already extracted tokens.
            // If the hash was just #/auth/reset-password, it's fine.
            // If it was #access_token=..., it should ideally be cleared.
            // However, `window.history.replaceState` with just `pathname + search` clears the hash.
            // So, if `preserveHashForRouting` is true, we explicitly set `url.hash = currentLocation.hash`
            // if it doesn't contain sensitive tokens.
            // A simpler approach: if preserving, just ensure the route part is there.
            // The current logic in `reset-password.tsx` using `location.pathname + location.hash`
            // for `replaceState` when `code` is present is good.
            // This utility function's `cleanupAuthLinkUrl` might be more aggressive.
            // Let's stick to: if `preserveHashForRouting` is true, we only remove the `code` param.
            // The calling component is responsible for handling its specific hash needs.
            // For now, if `preserveHashForRouting` is true, we just don't clear `url.hash = ''`.
            // The `code` param removal is the main generic cleanup.
             console.log('[AuthLinkUtils] Preserving hash for routing. Current hash:', url.hash);
        }
    }
  }

  // Update the URL without triggering a page reload
  if (window.location.href !== url.href) {
    window.history.replaceState(null, document.title, url.toString());
    console.log('[AuthLinkUtils] URL updated to:', url.toString());
  } else {
    console.log('[AuthLinkUtils] URL did not need update.');
  }
}

/**
 * Determines the redirect URL based on user profile.
 * @param userProfile The user profile object, expected to have a userRole property.
 * @param env Environment variables, expected to have VITE_USER_DASHBOARD_URL.
 * @returns The redirect URL string.
 */
export function getRedirectUrlBasedOnRole(
  userProfile: { userRole?: string | null } | null,
  env: { VITE_USER_DASHBOARD_URL?: string }
): string {
  const defaultRedirect = '/admin/dashboard';
  if (!userProfile || !userProfile.userRole) {
    console.log('[AuthLinkUtils] No user profile or role found, defaulting to admin dashboard.');
    return defaultRedirect;
  }

  const isPartnerUser = userProfile.userRole.startsWith('entity_');
  const partnerDashboardUrl = env.VITE_USER_DASHBOARD_URL;

  if (isPartnerUser && partnerDashboardUrl) {
    console.log('[AuthLinkUtils] Partner user detected, redirecting to partner dashboard:', partnerDashboardUrl);
    return partnerDashboardUrl;
  }

  console.log('[AuthLinkUtils] Non-partner user or no partner URL, redirecting to admin dashboard.');
  return defaultRedirect;
}
